/**
 * BlDetailsEditor.jsx
 * Module: common
 *
 * Editable "Bill of Lading Details" grid for an ocean booking that's already
 * been confirmed — rendered inside the Documents popup on the Queries page.
 *
 * Seeds from the `blDetails` array stored on the booking, lets a KAM re-type
 * any field, append rows via "Add BL No" under the grid, and drop any row via
 * its own remove button. Save sends the whole array to
 * PUT kam/ocean-booking/bl-details, which
 * replaces the stored list on both the payment and the query record and
 * re-stamps the query's `Noofbl` from the new row count.
 *
 * `id` is the BL's 1-based position in that list, so adding and removing both
 * keep it contiguous — the stored ids always read 1..Noofbl.
 *
 * Read-only sibling of Bl_notable.jsx — that one captures these same rows at
 * confirm time, sized by the booking's "No. of BL"; this one edits them
 * afterwards, where the row count is whatever's been saved plus anything the
 * KAM adds. The visual language is deliberately shared between the two.
 */
import React, { useEffect, useRef, useState } from "react";
import { Table, Input, ConfigProvider } from "antd";
import { Plus, Trash2 } from "lucide-react";
import { useDispatch } from "react-redux";
// The host page owns the ToastContainer, not this component: a successful save
// closes the popup, which unmounts everything here — a container rendered
// locally would take the success toast down with it before it could be read.
import { toast } from "react-toastify";
import { updateOceanBlDetails } from "../../store/SearchRates/SearchRatesSlice";

const AMBER = "#EF9F27";
const AMBER_HEADER_BG = "#FFFBEB";   // amber-50 — soft cream instead of full orange fill
const AMBER_HEADER_TEXT = "#92400E"; // amber-800 — readable dark-amber on cream
const AMBER_BORDER = "#FDE68A";      // amber-200 — light divider between header cells

const TOAST_OPTIONS = {
  position: "top-center",
  autoClose: 1500,
  hideProgressBar: true,
};

/** The three editable columns, in display order. */
const BL_FIELDS = [
  {
    key: "confirmationNumber",
    title: "Confirmation number",
    placeholder: "Enter confirmation number",
  },
  {
    key: "containerNumber",
    title: "Container number",
    placeholder: "Enter container number",
  },
  { key: "blNumber", title: "BL number", placeholder: "Enter BL number" },
];

/**
 * Shape the saved list into editable rows: every field becomes a controlled
 * string, and a row missing `id` (saved before that field existed) falls back
 * to its 1-based position.
 * @param {Array} list - blDetails as stored on the booking record
 */
const toRows = (list) =>
  (Array.isArray(list) ? list : []).map((row, index) => ({
    id: Number(row?.id) > 0 ? Number(row.id) : index + 1,
    confirmationNumber: row?.confirmationNumber ?? "",
    containerNumber: row?.containerNumber ?? "",
    blNumber: row?.blNumber ?? "",
  }));

/**
 * Value-based fingerprint of a row list. Used both to flag unsaved edits and to
 * re-seed the grid only when the *contents* of the saved list change — the
 * parent can hand back a fresh array on any render, so an identity check there
 * would wipe whatever the KAM was typing.
 * @param {Array} rows - rows from toRows()
 */
const signature = (rows) =>
  JSON.stringify(
    rows.map((row) => [
      row.id,
      row.confirmationNumber,
      row.containerNumber,
      row.blNumber,
    ]),
  );

/** Strip the rows down to what the API stores, trimming each field. */
const toPayload = (rows) =>
  rows.map((row, index) => ({
    id: Number(row.id) > 0 ? Number(row.id) : index + 1,
    confirmationNumber: String(row.confirmationNumber ?? "").trim(),
    containerNumber: String(row.containerNumber ?? "").trim(),
    blNumber: String(row.blNumber ?? "").trim(),
  }));

/**
 * @param {Object} props
 * @param {string} props.queryId - booking's query id, sent with the update
 * @param {Array} [props.blDetails] - rows currently stored on the booking
 * @param {Function} [props.onSaved] - receives the normalised list the backend
 *   stored, so the caller can keep its own copy of the record in step
 */
export default function BlDetailsEditor({ queryId, blDetails, onSaved }) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState(() => toRows(blDetails));
  const [saving, setSaving] = useState(false);

  // Recomputed every render — these lists are a handful of rows, so this is
  // cheaper than memoising around an unstable `blDetails` identity.
  const savedSignature = signature(toRows(blDetails));
  const isDirty = signature(rows) !== savedSignature;

  // Read through a ref so the effect can depend on the *value* fingerprint
  // alone — see signature() above for why the array identity can't be trusted.
  const blDetailsRef = useRef(blDetails);
  blDetailsRef.current = blDetails;

  useEffect(() => {
    setRows(toRows(blDetailsRef.current));
  }, [savedSignature]);

  const handleFieldChange = (index, field, value) => {
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  // A new BL continues the existing numbering rather than reusing a position,
  // so ids stay unique even if the saved list ever arrives out of order.
  const handleAddRow = () => {
    setRows((current) => {
      const highestId = current.reduce(
        (max, row) => (Number(row.id) > max ? Number(row.id) : max),
        0,
      );
      return [
        ...current,
        {
          id: highestId + 1,
          confirmationNumber: "",
          containerNumber: "",
          blNumber: "",
        },
      ];
    });
  };

  // Dropping a slot renumbers everything after it: `id` is the BL's position in
  // the list, and the saved count is what the query's `Noofbl` gets re-stamped
  // from — so removing BL 2 of 4 has to leave 1..3, not a gap where 2 was.
  // Nothing is written until Save, so this is undone by closing the popup.
  const handleRemoveRow = (index) => {
    setRows((current) =>
      current
        .filter((_row, i) => i !== index)
        .map((row, i) => ({ ...row, id: i + 1 })),
    );
  };

  const handleSave = () => {
    if (!queryId || saving) return;
    setSaving(true);
    dispatch(updateOceanBlDetails({ queryId, blDetails: toPayload(rows) }))
      .then((res) => {
        setSaving(false);
        // The thunk swallows axios errors and returns `err.response`, so a
        // failure arrives as a fulfilled payload — check the body, not .catch.
        const body = res?.payload?.data;
        const saved = body?.data?.blDetails;
        if (body?.statusCode === 200 && Array.isArray(saved)) {
          toast.success(
            body?.message || "BL details updated successfully!",
            TOAST_OPTIONS,
          );
          if (onSaved) onSaved(saved);
          return;
        }
        toast.error(
          body?.message || "Failed to update BL details",
          TOAST_OPTIONS,
        );
      })
      .catch(() => {
        setSaving(false);
        toast.error("Failed to update BL details", TOAST_OPTIONS);
      });
  };

  const columns = [
    // Row label ("1", "2", …) so a multi-BL grid reads as a numbered list.
    // `id` is the BL number saved with the row, not its screen position.
    {
      title: "BL",
      dataIndex: "id",
      key: "blLabel",
      align: "center",
      width: 70,
      render: (id) => (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-amber-50 border border-amber-200 text-xs font-bold text-amber-700 select-none">
          {id}
        </span>
      ),
    },
    ...BL_FIELDS.map((field) => ({
      title: field.title,
      dataIndex: field.key,
      key: field.key,
      align: "left",
      render: (text, _record, index) => (
        <Input
          value={text}
          placeholder={field.placeholder}
          disabled={saving}
          style={{ width: "90%" }}
          onChange={(e) => handleFieldChange(index, field.key, e.target.value)}
        />
      ),
    })),
    // Every row carries its own remove; adding is a single action for the whole
    // grid, so it lives under the table rather than in one arbitrary row's cell.
    {
      title: "",
      key: "rowAction",
      align: "center",
      width: 64,
      render: (_text, _record, index) => (
        <button
          type="button"
          title="Remove this BL"
          aria-label="Remove this BL"
          disabled={saving}
          onClick={() => handleRemoveRow(index)}
          className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-red-200 bg-red-50 text-red-500 transition-colors hover:bg-red-100 hover:text-red-600 disabled:opacity-40"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* ── Section header ── */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <span className="w-1 h-5 rounded-full bg-[#EF9F27] shrink-0" />
          <svg
            className="w-4 h-4 text-gray-400 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="text-sm font-bold text-gray-800 tracking-tight">
            Bill of Lading Details
          </h3>
        </div>

        <div className="flex items-center gap-2">
          {/* BL count pill */}
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
            {rows.length} BL{rows.length !== 1 ? "s" : ""}
          </span>

          {/* Adding is a whole-grid action, so it sits in the card header
              beside the count it changes rather than below the rows. */}
          <button
            type="button"
            onClick={handleAddRow}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Add BL No
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="p-4">
        <ConfigProvider
          theme={{
            token: { colorPrimary: AMBER, borderRadiusLG: 8 },
            components: {
              Table: {
                headerBg: AMBER_HEADER_BG,
                headerColor: AMBER_HEADER_TEXT,
                headerSplitColor: AMBER_BORDER,
                borderColor: "#F3F4F6",
                borderRadius: 8,
                rowHoverBg: "#FFFBEB",
              },
              Input: {
                borderRadius: 8,
                hoverBorderColor: AMBER,
                activeBorderColor: AMBER,
                activeShadow: `0 0 0 2px rgba(239, 159, 39, 0.15)`,
              },
            },
          }}
        >
          <Table
            columns={columns}
            dataSource={rows}
            pagination={false}
            bordered
            size="middle"
            rowKey="id"
            // Safety net for the narrow modal — the columns fit at the popup's
            // current width, but scroll rather than squash below it.
            scroll={{ x: 700 }}
            locale={{
              emptyText: "No BLs yet — use “Add BL No” to add the first one.",
            }}
          />
        </ConfigProvider>

        {/* ── Actions ── */}
        {/* Save stays enabled whether or not anything changed — re-sending an
            unchanged grid is harmless (the backend replaces the list
            wholesale), and a disabled primary button reads as "broken" more
            often than it reads as "no edits". The empty left-hand div is what
            keeps Save pinned right when there's no unsaved-changes hint. */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
          <div className="flex items-center gap-2">
            {isDirty && !saving && (
              <span className="text-[11px] font-medium text-amber-600">
                Unsaved changes
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#EF9F27] px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save BL Details"}
          </button>
        </div>
      </div>
    </div>
  );
}
